import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DataTable, type Column } from '../components/DataTable';
import { Icon } from '../components/icons';
import { Badge, EmptyState, ErrorState, Field, LoadingRows, PageHeader, Segmented } from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errors';
import { formatDateTime, formatPhone, slaRemaining } from '../lib/format';
import { qk, useTicket, useTickets } from '../lib/queries';
import type { Ticket } from '../lib/types';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Tümü' },
  { value: 'open', label: 'Açık' },
  { value: 'pending', label: 'Bekliyor' },
  { value: 'resolved', label: 'Çözüldü' }
];

function statusBadge(ticket: Ticket) {
  if (ticket.status === 'resolved') return <Badge tone="ok">çözüldü</Badge>;
  if (ticket.status === 'pending') return <Badge tone="warn">bekliyor</Badge>;
  return <Badge>açık</Badge>;
}

function priorityBadge(ticket: Ticket) {
  if (ticket.priority === 'high') return <Badge tone="danger">yüksek</Badge>;
  if (ticket.priority === 'low') return <Badge>düşük</Badge>;
  return <Badge>normal</Badge>;
}

function channelLabel(channel: Ticket['channel']) {
  if (channel === 'both') return 'WhatsApp + e-posta';
  return channel === 'whatsapp' ? 'WhatsApp' : 'E-posta';
}

/** S6 — Ticket listesi. */
export function TicketsPage() {
  const [status, setStatus] = useState('all');
  const tickets = useTickets(status);
  const navigate = useNavigate();

  const columns: Column<Ticket>[] = [
    { key: 'id', header: 'Ticket', width: '190px', render: (row) => <span className="mono text-[12px]">{row.no}</span> },
    {
      key: 'subject',
      header: 'Konu',
      render: (row) => (
        <span className="flex items-center gap-2">
          <span className="max-w-[380px] truncate">{row.subject}</span>
          {row.isTest ? <Badge tone="warn">Test</Badge> : null}
        </span>
      )
    },
    { key: 'channel', header: 'Kanal', width: '140px', render: (row) => <span className="text-[12px] text-muted">{channelLabel(row.channel)}</span> },
    { key: 'priority', header: 'Öncelik', width: '90px', render: (row) => priorityBadge(row) },
    {
      key: 'sla',
      header: 'SLA',
      width: '140px',
      render: (row) => {
        const sla = slaRemaining(row.slaDueAt);
        return <span className={'mono text-[12px] ' + (sla.late ? 'text-danger' : 'text-ink-2')}>{sla.text}</span>;
      }
    },
    { key: 'status', header: 'Durum', width: '100px', render: (row) => statusBadge(row) }
  ];

  return (
    <div>
      <PageHeader
        title="Ticket"
        subtitle="WhatsApp ve e-posta destek talepleri; SLA sayacı ile birlikte."
        actions={<Segmented value={status} options={STATUS_OPTIONS} onChange={setStatus} label="Ticket durumu" />}
      />
      {tickets.isError ? (
        <ErrorState message={errorMessage(tickets.error)} onRetry={() => void tickets.refetch()} />
      ) : tickets.isLoading ? (
        <LoadingRows rows={8} columns={5} />
      ) : (
        <DataTable
          caption="Ticket listesi"
          columns={columns}
          rows={tickets.data?.items ?? []}
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate('/tickets/' + row.id)}
          emptyTitle="Ticket yok"
          emptyHint="Bu durumda ticket bulunmuyor."
        />
      )}
    </div>
  );
}

/** S6 — Ticket detayı: WhatsApp + e-posta thread'i, kanal seçerek yanıt. */
export function TicketDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const ticket = useTicket(id);
  const toast = useToast();
  const queryClient = useQueryClient();
  const [channel, setChannel] = useState<'whatsapp' | 'email'>('whatsapp');
  const [reply, setReply] = useState('');

  const replyMutation = useMutation({
    mutationFn: (payload: { channel: 'whatsapp' | 'email'; body: string }) => api.ticketReply(id, payload.channel, payload.body),
    onSuccess: () => {
      setReply('');
      toast.push('Yanıt gönderildi; ticket beklemede durumuna alındı.', 'ok');
      void queryClient.invalidateQueries({ queryKey: qk.ticket(id) });
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const closeMutation = useMutation({
    mutationFn: () => api.ticketClose(id),
    onSuccess: () => {
      toast.push('Ticket kapatıldı.', 'ok');
      void queryClient.invalidateQueries({ queryKey: qk.ticket(id) });
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  if (ticket.isError) return <ErrorState message={errorMessage(ticket.error)} onRetry={() => void ticket.refetch()} />;
  if (ticket.isLoading) return <LoadingRows rows={8} columns={3} />;
  if (!ticket.data) return <EmptyState title="Ticket bulunamadı" />;

  const data = ticket.data;
  const sla = slaRemaining(data.slaDueAt);

  return (
    <div>
      <PageHeader
        title={data.subject}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span className="mono">{data.no}</span>
            <span>·</span>
            <span>{channelLabel(data.channel)}</span>
            <span>·</span>
            <span className={sla.late ? 'text-danger' : undefined}>SLA: {sla.text}</span>
          </span>
        }
        actions={
          <>
            <Link to="/tickets" className="btn btn-sm">
              <Icon name="arrowLeft" size={14} />
              Liste
            </Link>
            <button type="button" className="btn btn-sm" onClick={() => closeMutation.mutate()} disabled={data.status === 'resolved'}>
              <Icon name="check" size={14} />
              Kapat
            </button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 p-6 min-[1200px]:grid-cols-[minmax(0,1fr)_320px]">
        <section className="panel flex min-h-[420px] flex-col">
          <div className="panel-head">
            <h2 className="text-[13px]">Konuşma dökümü</h2>
            <span className="mono text-[11px] text-muted">{data.messages.length} mesaj</span>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {data.messages.map((message) => (
              <div key={message.id} className="rounded-[4px] border border-line p-2">
                <div className="flex items-center gap-2 text-[11px] text-muted">
                  <Badge tone={message.channel === 'email' ? 'neutral' : 'ok'}>
                    {message.channel === 'email' ? 'e-posta' : 'WhatsApp'}
                  </Badge>
                  <span className="font-medium text-ink-2">
                    {message.author === 'customer' ? 'Müşteri' : message.author === 'agent' ? 'Ajan' : 'AI'}
                  </span>
                  <span className="mono">{formatDateTime(message.at)}</span>
                </div>
                {message.subject ? <div className="mt-1 text-[12px] font-medium">{message.subject}</div> : null}
                <div className="mt-1 whitespace-pre-wrap text-[13px]">{message.body}</div>
              </div>
            ))}
          </div>

          <div className="border-t border-line px-4 py-3">
            <div className="mb-2 flex items-center gap-3">
              <span className="text-[12px] font-medium text-ink-2">Yanıt kanalı</span>
              <label className="flex items-center gap-1 text-[12px]">
                <input type="radio" name="channel" checked={channel === 'whatsapp'} onChange={() => setChannel('whatsapp')} />
                WhatsApp
              </label>
              <label className={'flex items-center gap-1 text-[12px] ' + (data.email ? '' : 'opacity-50')}>
                <input
                  type="radio"
                  name="channel"
                  checked={channel === 'email'}
                  disabled={!data.email}
                  onChange={() => setChannel('email')}
                />
                E-posta
              </label>
              {!data.email ? <span className="text-[11px] text-muted">E-posta adresi yok</span> : null}
            </div>
            <Field label="Yanıt" htmlFor="ticket-reply">
              <textarea
                id="ticket-reply"
                className="textarea"
                value={reply}
                onChange={(event) => setReply(event.target.value)}
                placeholder={channel === 'email' ? 'E-posta yanıtı…' : 'WhatsApp yanıtı…'}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                    event.preventDefault();
                    if (reply.trim()) replyMutation.mutate({ channel, body: reply.trim() });
                  }
                }}
              />
            </Field>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-[11px] text-muted">Ctrl+Enter ile gönder · Test modunda e-posta müşteriye gitmez</span>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={!reply.trim() || replyMutation.isPending}
                onClick={() => replyMutation.mutate({ channel, body: reply.trim() })}
              >
                <Icon name="send" size={14} />
                Yanıtla
              </button>
            </div>
          </div>
        </section>

        <aside className="space-y-4">
          <section className="panel">
            <div className="panel-head">
              <h2 className="text-[13px]">Özet</h2>
            </div>
            <div className="panel-body space-y-2 text-[12px]">
              <p className="leading-5 text-ink-2">{data.summary}</p>
              <div className="flex flex-wrap gap-1">
                {data.aiSources.map((source) => (
                  <Badge key={source}>{source}</Badge>
                ))}
              </div>
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2 className="text-[13px]">Kayıt</h2>
            </div>
            <dl className="divide-y divide-line text-[12px]">
              <div className="flex items-center justify-between px-4 py-2">
                <dt className="text-muted">Durum</dt>
                <dd>{statusBadge(data)}</dd>
              </div>
              <div className="flex items-center justify-between px-4 py-2">
                <dt className="text-muted">Öncelik</dt>
                <dd>{priorityBadge(data)}</dd>
              </div>
              <div className="flex items-center justify-between px-4 py-2">
                <dt className="text-muted">Telefon</dt>
                <dd className="mono">{formatPhone(data.phone)}</dd>
              </div>
              <div className="flex items-center justify-between px-4 py-2">
                <dt className="text-muted">E-posta</dt>
                <dd className="max-w-[180px] truncate">{data.email || '—'}</dd>
              </div>
              <div className="flex items-center justify-between px-4 py-2">
                <dt className="text-muted">Atanan</dt>
                <dd>{data.assignee ? data.assignee : 'Atanmadı'}</dd>
              </div>
              <div className="flex items-center justify-between px-4 py-2">
                <dt className="text-muted">Açılış</dt>
                <dd className="mono">{formatDateTime(data.createdAt)}</dd>
              </div>
            </dl>
          </section>

          {data.conversationId ? (
            <Link to={'/konusma/' + data.conversationId} className="btn w-full">
              <Icon name="inbox" size={14} />
              WhatsApp konuşmasını aç
            </Link>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
